import type { GeminiUsageMetadata } from '../services/geminiClient'
import { limitsLiftedForWorkspace } from './demoUnlock'
import { AI_MODEL_REGISTRY } from '../config/aiModelRegistry'
import type { AiModelId } from '../types/ai'

export type AiModelTierId = 'good' | 'better' | 'best'
/**
 * Billing accepts either a legacy generic tier id ('good'/'better'/'best'),
 * or any concrete model id present in aiModelRegistry.json. Pricing rows for
 * concrete models are sourced from the registry — no more duplicated rows
 * to keep in sync when a new model is added.
 */
export type AiBillingModel = AiModelTierId | AiModelId

export interface AiTokenUsage {
  inputTokens: number
  outputTokens: number
  totalTokens: number
  source: 'actual' | 'estimated'
}

export interface AiCreditQuote {
  providerCostUsd: number
  credits: number
  usage: AiTokenUsage
}

const CREDIT_VALUE_USD = 0.05
const TARGET_PROVIDER_COST_SHARE = 0.05
const PROVIDER_COST_PER_CREDIT_USD = CREDIT_VALUE_USD * TARGET_PROVIDER_COST_SHARE

// Legacy generic tier ids kept for callers that predate the concrete-model
// billing switch. New callers should always pass a concrete AiModelId.
const LEGACY_TIER_PRICING = {
  good: { input: 0.15, output: 1.25 },
  better: { input: 0.15, output: 1.25 },
  best: { input: 1.25, output: 10 },
} as const

// Concrete model pricing lives in aiModelRegistry.json — build a lookup once
// on module load so a hot path lookup is O(1). Adding a new model to the
// registry automatically extends billing coverage; no edit here required.
const REGISTRY_PRICING = new Map<string, { input: number; output: number }>()
for (const spec of AI_MODEL_REGISTRY) REGISTRY_PRICING.set(spec.id, spec.pricingUsdPerMillion)

const resolvePricing = (model: AiBillingModel): { input: number; output: number } => {
  if (model === 'good') return LEGACY_TIER_PRICING.good
  if (model === 'better') return LEGACY_TIER_PRICING.better
  if (model === 'best') return LEGACY_TIER_PRICING.best
  return REGISTRY_PRICING.get(model) ?? LEGACY_TIER_PRICING.good
}

export const estimateTokensFromText = (text: string): number => {
  const normalized = text.trim()
  if (!normalized) return 0
  return Math.max(1, Math.ceil(normalized.length / 4))
}

export const normalizeGeminiUsage = (
  usage: GeminiUsageMetadata | undefined,
  fallbackInputText: string,
  fallbackOutputText = '',
): AiTokenUsage => {
  const actualInput = usage?.promptTokenCount
  const actualOutput = (usage?.candidatesTokenCount ?? 0) + (usage?.thoughtsTokenCount ?? 0)
  const actualTotal = usage?.totalTokenCount
  if (
    typeof actualInput === 'number' &&
    Number.isFinite(actualInput) &&
    typeof actualTotal === 'number' &&
    Number.isFinite(actualTotal)
  ) {
    const outputTokens = actualOutput > 0 ? actualOutput : Math.max(0, actualTotal - actualInput)
    return {
      inputTokens: Math.max(0, Math.round(actualInput)),
      outputTokens: Math.max(0, Math.round(outputTokens)),
      totalTokens: Math.max(0, Math.round(actualTotal)),
      source: 'actual',
    }
  }

  const inputTokens = estimateTokensFromText(fallbackInputText)
  const outputTokens = estimateTokensFromText(fallbackOutputText)
  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    source: 'estimated',
  }
}

export const quoteAiCredits = (
  tier: AiBillingModel,
  usage: AiTokenUsage,
): AiCreditQuote => {
  const pricing = resolvePricing(tier)
  const providerCostUsd =
    (usage.inputTokens / 1_000_000) * pricing.input +
    (usage.outputTokens / 1_000_000) * pricing.output
  return {
    providerCostUsd,
    credits: Math.max(1, Math.ceil(providerCostUsd / PROVIDER_COST_PER_CREDIT_USD)),
    usage,
  }
}

export const estimateAiCreditsForText = (
  tier: AiBillingModel,
  inputText: string,
  expectedOutputTokens = 2048,
): AiCreditQuote => {
  const inputTokens = estimateTokensFromText(inputText)
  return quoteAiCredits(tier, {
    inputTokens,
    outputTokens: expectedOutputTokens,
    totalTokens: inputTokens + expectedOutputTokens,
    source: 'estimated',
  })
}

// `workspace` (optional): a demo-unlocked workspace (R35) skips the credit-balance check, as the testing override does.
export const canAttemptAiCall = (availableCredits: number, estimatedCredits: number, workspace?: unknown): boolean => {
  if (limitsLiftedForWorkspace(workspace)) return true
  return availableCredits >= estimatedCredits
}

export const clampBurnAmount = (availableCredits: number, actualCredits: number): number =>
  Math.max(0, Math.min(Math.floor(availableCredits), actualCredits))
